import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, LifeBuoy, MessageCircle, Send, Paperclip, X, ChevronLeft,
  CheckCircle2, AlertTriangle, Clock as ClockIcon, ChevronRight, Loader2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { TourVideoCard } from "@/components/TourVideo";
import { AiSupportTeamPanel, AiAnalysisBlock, KnownIssuesBoard } from "./AiSupport";

const STATUS_META = {
  open:           { label: "Open",           cls: "bg-blue-100 text-blue-800 border-blue-300" },
  in_progress:    { label: "In progress",    cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  awaiting_reply: { label: "Awaiting reply", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  closed:         { label: "Closed",         cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

function isImage(path) {
  return /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(path || "");
}

async function uploadFile(file, category = "support") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { path: data.storage_path, name: file.name };
}

export default function Support() {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "super_admin";

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/support_tickets", { params });
      setTickets(data);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((t) =>
      (t.subject || "").toLowerCase().includes(q) ||
      (t.opener_user_name || "").toLowerCase().includes(q) ||
      (t.org_name || "").toLowerCase().includes(q)
    );
  }, [tickets, search]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, awaiting_reply: 0, closed: 0 };
    for (const t of tickets) {
      if (c[t.status] !== undefined) c[t.status] += 1;
    }
    return c;
  }, [tickets]);

  return (
    <div data-testid="support-page" className="p-6 lg:p-10 max-w-7xl mx-auto pb-24">
      <PageHeader
        tag={isSuperAdmin ? "/ Platform support" : "/ Need a hand?"}
        title={isSuperAdmin ? "Support inbox" : "Support"}
        subtitle={isSuperAdmin
          ? "All tickets across every Fieldlink customer. Reply, change status, close out."
          : "Open a ticket for any bug or question. Our team will reply right here in the app."}
        action={
          <Button onClick={() => setCreateOpen(true)}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="new-ticket-button">
            <Plus className="w-4 h-4 mr-2" /> New ticket
          </Button>
        }
      />

      {!isSuperAdmin && <TourVideoCard />}
      {isSuperAdmin && <AiSupportTeamPanel />}
      {isSuperAdmin && <KnownIssuesBoard />}

      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="support-filter-row">
        {[
          { v: "all", label: `All · ${tickets.length}` },
          { v: "open", label: `Open · ${counts.open}` },
          { v: "in_progress", label: `In progress · ${counts.in_progress}` },
          { v: "awaiting_reply", label: `Awaiting reply · ${counts.awaiting_reply}` },
          { v: "closed", label: `Closed · ${counts.closed}` },
        ].map((f) => (
          <Button key={f.v} size="sm" variant={statusFilter === f.v ? "default" : "outline"}
                  className={`rounded-sm uppercase tracking-wider font-bold text-xs ${statusFilter === f.v ? "bg-zinc-900 text-white" : ""}`}
                  onClick={() => setStatusFilter(f.v)}
                  data-testid={`support-filter-${f.v}`}>
            {f.label}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[180px] max-w-sm ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search subject or sender…"
                 className="pl-9 rounded-sm border-zinc-400 h-9"
                 data-testid="support-search" />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No tickets yet"
          description={isSuperAdmin ? "Tickets opened by any org will land here." : "Open your first ticket and our team will jump on it."}
        />
      ) : (
        <div className="hard-card overflow-hidden">
          <ul className="divide-y divide-zinc-200" data-testid="ticket-list">
            {filtered.map((t) => (
              <TicketRow key={t.id} ticket={t} isSuperAdmin={isSuperAdmin} onOpen={() => setOpenId(t.id)} />
            ))}
          </ul>
        </div>
      )}

      <NewTicketDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      <TicketThreadDialog ticketId={openId} isSuperAdmin={isSuperAdmin} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

// ============================================================
//  TICKET ROW (list item)
// ============================================================
function TicketRow({ ticket, isSuperAdmin, onOpen }) {
  const s = STATUS_META[ticket.status] || STATUS_META.open;
  return (
    <li
      onClick={onOpen}
      className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 transition-colors"
      data-testid={`ticket-row-${ticket.id}`}
    >
      <div className="w-9 h-9 shrink-0 bg-zinc-100 flex items-center justify-center text-zinc-600">
        <LifeBuoy className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm truncate">{ticket.subject}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${s.cls}`}>
            {s.label}
          </span>
          {!isSuperAdmin && ticket.unread_for_user && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#FF5F15] text-white animate-pulse" data-testid={`ticket-unread-${ticket.id}`}>
              Reply waiting
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest mt-0.5 flex items-center gap-2 flex-wrap">
          <span><b>{ticket.opener_user_name}</b>{isSuperAdmin && ticket.org_name ? <> · {ticket.org_name}</> : null}</span>
          <span>·</span>
          <MessageCircle className="w-3 h-3" />
          <span>{ticket.message_count} msg{ticket.message_count === 1 ? "" : "s"}</span>
          {ticket.last_message_at && (
            <>
              <span>·</span>
              <span>{new Date(ticket.last_message_at).toLocaleString("en-NZ")}</span>
            </>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-400" />
    </li>
  );
}

// ============================================================
//  NEW TICKET DIALOG
// ============================================================
function NewTicketDialog({ open, onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => {
    if (open) { setSubject(""); setBody(""); setAttachments([]); }
  }, [open]);

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadBusy(true);
    try {
      const up = [];
      for (const f of files) up.push(await uploadFile(f));
      setAttachments((s) => [...s, ...up]);
      toast.success(`Attached ${up.length}`);
    } catch { toast.error("Upload failed"); }
    finally { setUploadBusy(false); e.target.value = ""; }
  };

  const removeAttach = (i) => setAttachments((s) => s.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!body.trim()) { toast.error("Describe the issue"); return; }
    setBusy(true);
    try {
      await api.post("/support_tickets", {
        subject: subject.trim(),
        body: body.trim(),
        attachments: attachments.map((a) => a.path),
      });
      toast.success("Ticket opened — we'll be in touch");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <LifeBuoy className="w-5 h-5" /> Open a ticket
          </DialogTitle>
          <DialogDescription>Tell us what's broken or what you need a hand with. We'll reply here in the app.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-tag">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   placeholder="e.g. Cannot export payroll PDF on Safari"
                   className="mt-2 rounded-sm border-zinc-400" required
                   data-testid="ticket-subject-input" />
          </div>
          <div>
            <Label className="label-tag">What's going on? *</Label>
            <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                      placeholder="Steps to reproduce, what you expected, what actually happened…"
                      className="mt-2 rounded-sm border-zinc-400" required
                      data-testid="ticket-body-input" />
          </div>
          <div>
            <Label className="label-tag">Attachments <span className="text-zinc-400 normal-case font-normal">(optional — screenshots, logs)</span></Label>
            <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-3 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]">
              <Paperclip className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">
                {uploadBusy ? "Uploading…" : "Attach files"}
              </span>
              <input type="file" multiple className="hidden" onChange={onAttach}
                     data-testid="ticket-attach-input" />
            </label>
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="ticket-attach-list">
                {attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 px-2 py-1 text-xs">
                    <Paperclip className="w-3 h-3" />
                    <span className="font-mono truncate flex-1">{a.name}</span>
                    <button type="button" onClick={() => removeAttach(i)} className="text-zinc-400 hover:text-red-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="ticket-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Open ticket</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  TICKET THREAD DIALOG
// ============================================================
function TicketThreadDialog({ ticketId, isSuperAdmin, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const scrollRef = useRef(null);

  const refresh = async () => {
    if (!ticketId) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/support_tickets/${ticketId}`);
      setData(data);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch { toast.error("Failed to load"); onClose(); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    setReply(""); setAttachments([]);
    // eslint-disable-next-line
  }, [ticketId]);

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadBusy(true);
    try {
      const up = [];
      for (const f of files) up.push(await uploadFile(f));
      setAttachments((s) => [...s, ...up]);
    } catch { toast.error("Upload failed"); }
    finally { setUploadBusy(false); e.target.value = ""; }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support_tickets/${ticketId}/messages`, {
        body: reply.trim(),
        attachments: attachments.map((a) => a.path),
      });
      setReply(""); setAttachments([]);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/support_tickets/${ticketId}`, { status: newStatus });
      toast.success(`Ticket marked ${STATUS_META[newStatus].label}`);
      await refresh();
      onChanged?.();
    } catch { toast.error("Status update failed"); }
  };

  if (!ticketId) return null;
  const s = data ? STATUS_META[data.status] || STATUS_META.open : null;
  const closed = data?.status === "closed";

  return (
    <Dialog open={!!ticketId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="ticket-thread-dialog">
        {/* Header */}
        <div className="px-5 py-4 border-b-2 border-zinc-900">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display uppercase tracking-tight text-xl leading-tight truncate">
                {data?.subject || "Ticket"}
              </DialogTitle>
              {data && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                  Opened by <b className="text-zinc-700 normal-case">{data.opener_user_name}</b>
                  {isSuperAdmin && data.org_name && <> · {data.org_name}</>}
                  · {new Date(data.created_at).toLocaleString("en-NZ")}
                </div>
              )}
            </div>
            {s && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${s.cls}`}>
                {s.label}
              </span>
            )}
          </div>

          {/* Super-admin status controls */}
          {isSuperAdmin && data && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ticket-status-controls">
              {Object.entries(STATUS_META).map(([k, v]) => (
                <Button
                  key={k}
                  size="sm"
                  variant={data.status === k ? "default" : "outline"}
                  className={`rounded-sm text-[10px] font-bold uppercase tracking-wider h-7 ${data.status === k ? "bg-zinc-900 text-white" : ""}`}
                  onClick={() => changeStatus(k)}
                  data-testid={`ticket-set-status-${k}`}
                >
                  {v.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 bg-zinc-50 space-y-3" data-testid="ticket-message-list">
          {loading ? (
            <div className="py-10 text-center text-sm text-zinc-500 font-mono">Loading…</div>
          ) : data?.messages?.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">No messages yet.</div>
          ) : (
            data?.messages.map((m) => <MessageBubble key={m.id} msg={m} me={user.id} />)
          )}
        </div>

        {/* Reply composer */}
        {!closed ? (
          <div className="border-t-2 border-zinc-900 bg-white p-3" data-testid="ticket-reply-composer">
            {isSuperAdmin && data && (
              <AiAnalysisBlock ticket={data} onUseDraft={(d) => setReply(d)} onChanged={refresh} />
            )}
            <Textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={isSuperAdmin ? "Reply as super admin…" : "Reply to the team…"}
              className="rounded-sm border-zinc-400"
              data-testid="ticket-reply-input"
            />
            {attachments.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {attachments.map((a, i) => (
                  <li key={i} className="bg-zinc-100 border border-zinc-300 px-2 py-0.5 text-[10px] font-mono flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {a.name}
                    <button type="button" onClick={() => setAttachments((s) => s.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-600 cursor-pointer hover:text-[#FF5F15]">
                <Paperclip className="w-3.5 h-3.5" />
                {uploadBusy ? "Uploading…" : "Attach"}
                <input type="file" multiple className="hidden" onChange={onAttach} data-testid="ticket-reply-attach" />
              </label>
              <Button size="sm" onClick={send} disabled={sending || !reply.trim()}
                      className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                      data-testid="ticket-reply-send">
                {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t-2 border-zinc-900 bg-zinc-100 p-3 text-center" data-testid="ticket-closed-banner">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Ticket closed
              {data?.closed_by_name && <span className="text-zinc-500 normal-case font-normal"> · by {data.closed_by_name}</span>}
            </div>
            {isSuperAdmin && (
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={() => changeStatus("open")}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid="ticket-reopen">
                  Re-open ticket
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MessageBubble({ msg, me }) {
  const fromMe = msg.sender_user_id === me;
  const isSuper = msg.sender_role === "super_admin";
  return (
    <div className={`flex ${fromMe ? "justify-end" : "justify-start"}`} data-testid={`ticket-message-${msg.id}`}>
      <div className={`max-w-[80%] ${fromMe ? "items-end" : "items-start"} flex flex-col`}>
        <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${fromMe ? "text-zinc-500" : isSuper ? "text-[#FF5F15] font-bold" : "text-zinc-600"}`}>
          {isSuper && <span className="bg-[#FF5F15] text-white px-1 mr-1">Fieldlink Support</span>}
          {msg.sender_user_name} · {new Date(msg.created_at).toLocaleString("en-NZ")}
          {msg.source === "email" && <span className="ml-1 border border-zinc-300 text-zinc-500 px-1 rounded-sm" data-testid={`msg-via-email-${msg.id}`}>via email</span>}
        </div>
        <div className={`p-3 ${fromMe ? "bg-[#09090B] text-white border-zinc-900" : isSuper ? "bg-white border-[#FF5F15] border-l-4" : "bg-white border-zinc-300"} border-2`}>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.body}</p>
          {msg.attachments && msg.attachments.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {msg.attachments.map((path, i) => (
                <Attachment key={i} path={path} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Attachment({ path }) {
  if (isImage(path)) {
    return (
      <AuthImage path={path} alt="attachment" className="w-full max-h-32 object-cover border border-zinc-300" />
    );
  }
  const name = path.split("/").pop();
  return (
    <a
      href={`/api/files/view?path=${encodeURIComponent(path)}&auth=${localStorage.getItem("fl_token")}`}
      target="_blank" rel="noreferrer"
      className="text-[11px] font-mono underline text-[#FF5F15] inline-flex items-center gap-1 bg-zinc-100 border border-zinc-300 px-2 py-1"
    >
      <Paperclip className="w-3 h-3" /> {name}
    </a>
  );
}
